import React, { useCallback, useMemo } from 'react';

import {
  Form,
  useCreate,
  useListContext,
  useNotify,
  usePermissions,
} from 'react-admin';

import {
  Button,
  Dialog,
  DialogTitle,
} from '@mui/material';

import { FieldValues } from 'react-hook-form';

import PublishIcon from '@mui/icons-material/Publish';

import useDialogStatus from '../../../../hooks/useDialogStatus';

import { RuleTypes } from '../../../../constants/ruleTypes';
import actions from '../../../../constants/actions';
import { getMatchingPermissions } from '../../../../utilities/hasAccess';
import { Permission } from '../../../../utilities/Authprovider';
import usePendingReview from '../../../../hooks/usePendingReview';
import errorNotification from '../../../../utilities/errorNotification';
import ActionFormContent from './ActionFormContent';

const Publish = ({
  ruleType,
  onPublished,
} : {
  ruleType: RuleTypes;
  onPublished: (isTestVersion: boolean) => void;
}) => {
  const { refetch } = useListContext();
  const notify = useNotify();
  const { permissions } = usePermissions<Permission[]>();

  const requirePeerReview = useMemo(() => {
    const matchingPermissions = getMatchingPermissions(permissions, actions.RULE_VERSION_PUBLISH);
    const firstMatch = matchingPermissions[0];
    if (!firstMatch || typeof firstMatch.attributes !== 'object' || firstMatch.attributes === null) {
      return false;
    }
    return (firstMatch.attributes as { requirePeerReview?: boolean }).requirePeerReview === true;
  }, [permissions]);

  const { open, openDialog, closeDialog } = useDialogStatus();
  const {
    pendingReview,
    isLoading: isPendingReviewLoading,
    refetch: refetchPendingReview,
  } = usePendingReview(
    actions.RULE_VERSION_PUBLISH,
    { ruleType },
    { enabled: open && requirePeerReview },
  );
  const [publish, { isLoading: isPublishing }] = useCreate();

  const handelSave = useCallback((data: FieldValues) => {
    if (isPublishing) return;

    if (requirePeerReview && !data.isTestVersion) {
      publish('reviews', { data: { action: actions.RULE_VERSION_PUBLISH, reasoning: data.reasoning, actionInformation: { ruleType, description: data.description } } }, {
        onSuccess: () => {
          notify('Review submitted successfully', { type: 'success' });
          closeDialog();
          refetchPendingReview();
        },
        onError: (error) => {
          notify(errorNotification(error, 'Failed to submit review'), { type: 'error' });
          refetchPendingReview();
        },
      });
    } else {
      publish('rules/publish', { data: { ...data, ruleType } }, {
        onSuccess: () => {
          notify('Rule draft was published', { type: 'success' });
          closeDialog();
          refetch();
          onPublished(!!data.isTestVersion);
        },
        onError: (error) => {
          notify(errorNotification(error, 'Rule draft could not be published'), { type: 'error' });
          refetch();
        },
      });
    }
  }, [
    closeDialog, isPublishing, notify, publish, onPublished,
    refetch, refetchPendingReview, ruleType, requirePeerReview,
  ]);

  return (
    <>
      <Button
        startIcon={<PublishIcon />}
        onClick={openDialog}
      >
        Publish
      </Button>
      <Dialog onClose={closeDialog} open={open} fullWidth maxWidth="sm">
        <Form
          onSubmit={handelSave}
          record={{ description: '', isTestVersion: false, reasoning: '' }}
        >
          <DialogTitle>Publish rule tree draft</DialogTitle>
          <ActionFormContent
            requirePeerReview={requirePeerReview}
            isSubmitting={isPublishing}
            pendingReview={open ? pendingReview : null}
            pendingReviewLoading={open ? isPendingReviewLoading : false}
            closeDialog={closeDialog}
            isPublish
          />
        </Form>
      </Dialog>
    </>
  );
};

export default Publish;
